import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { toast } from '@/lib/notifications';
import { createResponsibleCallable } from '@/lib/callables';

type Props = {
  open: boolean;
  tenantId: string;
  onClose: () => void;
};

/**
 * Cadastra um responsável pelo painel super-admin (`createResponsible`).
 *
 * Origem: sem issue — commit f948c64e4, 2026-06-10 18:39 +0200 (abas
 * Usuários e Responsáveis na página do tenant).
 *
 * Contrato: `createResponsible({ tenantId, name })`, nome de 2 a 80
 * caracteres; o novo responsável aparece pelo listener da aba.
 *
 * Usado por: `features/responsibles/ResponsiblesTab.tsx`.
 *
 * Falhas conhecidas:
 *  - o tratamento de erro procura `'already-exists'` dentro de
 *    `err.message`, mas o `FirebaseError` traz ali o texto do servidor
 *    ("Já existe um responsável com esse nome.") e o código em `err.code`. A mensagem específica nunca
 *    aparece (ver #926, achados).
 */
export function ResponsibleAddDialog({ open, tenantId, onClose }: Props): React.JSX.Element | null {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName('');
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  const canSubmit = name.trim().length >= 2 && name.trim().length <= 80;

  async function submit(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      await createResponsibleCallable({ tenantId, name: name.trim() });
      toast.success(t('adminResponsibles.toast.added'));
      onClose();
    } catch (err) {
      const code = err instanceof Error ? err.message : String(err);
      setError(
        code.includes('already-exists')
          ? t('adminResponsibles.errors.alreadyExists')
          : t('adminResponsibles.errors.addFailed'),
      );
      console.error('responsibleAdd submit failed', err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      visible={open}
      onDismiss={(): void => {
        if (!submitting) onClose();
      }}
      header={t('adminResponsibles.addDialog.title')}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose} disabled={submitting}>
              {t('adminResponsibles.addDialog.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={(): void => {
                void submit();
              }}
              loading={submitting}
              disabled={!canSubmit}
            >
              {t('adminResponsibles.addDialog.submit')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <FormField label={t('adminResponsibles.addDialog.name')}>
          <Input
            value={name}
            onChange={({ detail }): void => {
              setName(detail.value);
            }}
            disabled={submitting}
            placeholder={t('adminResponsibles.addDialog.namePlaceholder')}
          />
        </FormField>
        {error && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
